/**
 * Sun Service for TeeSync
 *
 * Calculates sunrise and sunset times using the suncalc library
 * (NOAA solar position algorithms) and formats them in the golf
 * course's actual IANA timezone via tz-lookup + Intl.DateTimeFormat.
 */

import SunCalc from 'suncalc';
import tzlookup from 'tz-lookup';
import { DAYLIGHT_CONFIG, categoriseScore } from './scoringConfig.js';

/**
 * Resolve the IANA timezone for a coordinate, falling back to UTC
 * if tz-lookup can't classify the point (e.g. open ocean).
 */
function resolveTimeZone(lat, lon) {
  try {
    return tzlookup(lat, lon);
  } catch {
    return 'UTC';
  }
}

/**
 * Format a UTC Date as a local-wall-clock HH:MM string in the given IANA tz.
 */
function formatLocalHHMM(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(date);

  const hour = parts.find(p => p.type === 'hour')?.value ?? '00';
  const minute = parts.find(p => p.type === 'minute')?.value ?? '00';
  return `${hour === '24' ? '00' : hour}:${minute}`;
}

/**
 * Get sunrise and sunset times for a specific date and location.
 *
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Object|null} {
 *   sunrise: "HH:MM" local at course,
 *   sunset:  "HH:MM" local at course,
 *   sunriseUTC: Date (true UTC),
 *   sunsetUTC:  Date (true UTC),
 *   timeZone:   IANA timezone string
 * }
 */
export function getSunTimes(dateStr, lat, lon) {
  try {
    if (!dateStr || lat === undefined || lon === undefined) {
      return null;
    }

    // Use noon UTC on the requested date so SunCalc picks the correct solar day
    // regardless of the executing machine's local tz.
    const date = new Date(`${dateStr}T12:00:00Z`);

    if (isNaN(date.getTime())) {
      console.warn('Invalid date for sun calculation:', dateStr);
      return null;
    }

    const times = SunCalc.getTimes(date, lat, lon);

    if (!times.sunrise || !times.sunset || isNaN(times.sunrise.getTime()) || isNaN(times.sunset.getTime())) {
      // Polar regions during midnight sun / polar night
      console.warn('Unable to calculate sun times for:', { dateStr, lat, lon });
      return null;
    }

    const timeZone = resolveTimeZone(lat, lon);

    return {
      sunrise: formatLocalHHMM(times.sunrise, timeZone),
      sunset: formatLocalHHMM(times.sunset, timeZone),
      sunriseUTC: times.sunrise,
      sunsetUTC: times.sunset,
      timeZone
    };
  } catch (error) {
    console.error('Error calculating sun times:', error);
    return null;
  }
}

/**
 * Calculate daylight score for a golf round using smooth mathematical curves.
 *
 * - Overlap subscore (70%): fraction of round within the safe daylight window
 *   (sunrise+30min to sunset-15min, local at the course)
 * - Comfort subscore (30%): SYMMETRIC logistic penalty as the round midpoint
 *   nears EITHER daylight edge (min of sunrise-side and sunset-side logistics),
 *   so a pre-dawn round is penalised as hard as a post-sunset one
 *
 * All times are reasoned about in local-minutes-since-midnight at the course.
 *
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} startTime - Tee time in HH:MM format (local at the course)
 * @param {number} durationHours - Round duration in hours
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Object} { score: number 0-10, category: string, reason: string }
 */
export function calculateDaylightScore(dateStr, startTime, durationHours, lat, lon) {
  try {
    const sunTimes = getSunTimes(dateStr, lat, lon);

    if (!sunTimes) {
      return {
        score: DAYLIGHT_CONFIG.neutralScore,
        category: 'unknown',
        reason: 'Sun data unavailable',
        overlapFraction: null,
        teeOffBeforeSunriseMin: null,
        teeOffAfterSunsetMin: null
      };
    }

    const [startHour, startMin] = startTime.split(':').map(Number);
    const teeTimeMinutes = startHour * 60 + startMin;

    const roundDurationMinutes = durationHours * 60;
    const roundEndMinutes = teeTimeMinutes + roundDurationMinutes;
    const roundMidpointMinutes = teeTimeMinutes + roundDurationMinutes / 2;

    // Parse the local HH:MM strings into minutes-since-midnight
    const [sunriseHour, sunriseMin] = sunTimes.sunrise.split(':').map(Number);
    const [sunsetHour, sunsetMin] = sunTimes.sunset.split(':').map(Number);
    const sunriseMinutes = sunriseHour * 60 + sunriseMin;
    const sunsetMinutes = sunsetHour * 60 + sunsetMin;

    // Safe play window: configurable margins after sunrise / before sunset
    const safeStartMinutes = sunriseMinutes + DAYLIGHT_CONFIG.safeStartOffsetMin;
    const safeEndMinutes = sunsetMinutes + DAYLIGHT_CONFIG.safeEndOffsetMin;

    // Overlap subscore
    const overlapStart = Math.max(teeTimeMinutes, safeStartMinutes);
    const overlapEnd = Math.min(roundEndMinutes, safeEndMinutes);
    const overlapMinutes = Math.max(0, overlapEnd - overlapStart);
    const overlapFraction = roundDurationMinutes > 0 ? overlapMinutes / roundDurationMinutes : 0;

    // Comfort subscore - SYMMETRIC logistic on how far the round midpoint sits
    // from each edge of the safe window. Taking the min means the score
    // collapses near (or past) EITHER edge: a pre-dawn round is penalised just
    // as hard as a post-sunset one, rather than scoring full marks for being
    // "well before sunset" as the old one-sided model did.
    const { steepness, midpointHours } = DAYLIGHT_CONFIG.proximityLogistic;
    const hoursAfterSafeStart = (roundMidpointMinutes - safeStartMinutes) / 60;
    const hoursBeforeSafeEnd = (safeEndMinutes - roundMidpointMinutes) / 60;
    const sunriseProximityScore = 1 / (1 + Math.exp(-steepness * (hoursAfterSafeStart - midpointHours)));
    const sunsetProximityScore = 1 / (1 + Math.exp(-steepness * (hoursBeforeSafeEnd - midpointHours)));
    const comfortScore = Math.min(sunriseProximityScore, sunsetProximityScore);

    const combinedScore = (
      overlapFraction * DAYLIGHT_CONFIG.overlapWeight +
      comfortScore * DAYLIGHT_CONFIG.comfortWeight
    ) * 10;
    let finalScore = Math.round(combinedScore * 10) / 10;

    // Hard cap for rounds that genuinely straddle the daylight boundary -
    // the smooth subscores can still leave these scoring in the "good" range.
    const startsBeforeSunrise = teeTimeMinutes < sunriseMinutes;
    const finishesAfterSunset = roundEndMinutes > sunsetMinutes;
    if (startsBeforeSunrise || finishesAfterSunset) {
      finalScore = Math.min(finalScore, DAYLIGHT_CONFIG.hardCapScore);
    }

    const category = categoriseScore(finalScore);
    let reason;
    if (overlapFraction <= 0) {
      reason = 'No daylight during round';
    } else if (startsBeforeSunrise) {
      reason = 'Starts before sunrise';
    } else if (finishesAfterSunset) {
      reason = 'Finishes after sunset';
    } else if (category === 'excellent') reason = 'Full daylight with comfortable margin';
    else if (category === 'very-good') reason = 'Full daylight throughout round';
    else if (category === 'good') reason = 'Good daylight conditions';
    else if (category === 'fair') reason = 'Round near sunrise or sunset';
    else if (category === 'poor') reason = 'Limited daylight for round';
    else reason = 'Insufficient daylight';

    return {
      score: finalScore,
      category,
      reason,
      overlapFraction,
      // Positive when the tee-off precedes sunrise / follows sunset; consumed
      // by the smart-search tee-off gate in roundService.
      teeOffBeforeSunriseMin: sunriseMinutes - teeTimeMinutes,
      teeOffAfterSunsetMin: teeTimeMinutes - sunsetMinutes
    };

  } catch (error) {
    console.error('Error calculating daylight score:', error);
    return {
      score: DAYLIGHT_CONFIG.neutralScore,
      category: 'unknown',
      reason: 'Calculation error',
      overlapFraction: null,
      teeOffBeforeSunriseMin: null,
      teeOffAfterSunsetMin: null
    };
  }
}

/**
 * Get daylight category for UI colour coding.
 */
export function getDaylightCategory(dateStr, startTime, durationHours, lat, lon) {
  const result = calculateDaylightScore(dateStr, startTime, durationHours, lat, lon);
  return result.category;
}

/**
 * Check if sun calculation is available for given coordinates.
 */
export function isSunDataAvailable(lat, lon) {
  return typeof lat === 'number' && typeof lon === 'number' &&
         lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}
